<template>
  <ModalStep
    id="group-structure-content"
    data-test-id="create-groupe_title-popup-create-groupe"
    :header="{
      title: !!groupIdRef
        ? `${t('admin.groups.groupStructureModal.existing.title')}`
        : `${t('admin.groups.groupStructureModal.new.title')}`,
    }"
  >
    <template #headerAfterElement>
      <ProgramsDropdown v-if="!groupIdRef" />
    </template>
    <template #bodyContent>
      <LoadingContent v-if="isGroupPendingRef && !!groupIdRef" />
      <template v-else>
        <div class="group-structure-listing">
          <!-- MANAGERS -->
          <div class="c-info-wrapper manager-listing">
            <p class="c-info-label">{{ t('common.manager.capitalize', 2) }}</p>
            <template v-if="managersInGroup.length">
              <UserCard v-for="manager in managersInGroup" :user="manager.user" :key="manager.user.id" :subInfo="' '">
                <template #actions>
                  <div
                    v-if="!isUserAccountManagerRef"
                    class="group-structure-user-remove-btn"
                    data-action=""
                    @click.stop="removeUserRole(manager.user.id, GroupUserRolesType.MANAGER)"
                  >
                    <CrossIcon data-test-id="btn-remove-user" color="danger" />
                  </div>
                </template>
              </UserCard>
            </template>
            <CustomButton
              v-if="!isManagerSearchEnabledRef && !isUserAccountManagerRef"
              usage="button"
              data-test-id="create-groupe_input-add-manager"
              type="button"
              color="secondary"
              :text="t('admin.groups.groupStructureModal.body.addManager')"
              @emit:click="isManagerSearchEnabledRef = true"
            >
              <template #icon>
                <CrossIcon color="primary" :rotate="45" />
              </template>
            </CustomButton>

            <!--
              Search excludes only users who already have the same role,
              allowing role changes (e.g., mentor -> manager)
            -->
            <UsersSearch
              :onMountFocus="true"
              :filterByRole="RoleEnumTypeV2.MANAGER"
              :remove-from-results="managersInGroup.map((m) => m.user)"
              v-if="isManagerSearchEnabledRef"
              :search-callback="managerSearchCallback"
              @emit:result="addManager"
            />
          </div>

          <div class="c-info-wrapper mentor-listing">
            <p class="c-info-label">{{ t('common.mentor.capitalize') }}</p>
            <UserCard v-if="mentorInGroup" :user="mentorInGroup.user" :subInfo="' '">
              <template #actions>
                <div
                  class="group-structure-user-remove-btn"
                  data-action=""
                  @click.stop="removeUserRole(mentorInGroup.user.id, GroupUserRolesType.MENTOR)"
                >
                  <CrossIcon data-test-id="btn-remove-user" color="danger" />
                </div>
              </template>
            </UserCard>
            <CustomButton
              v-if="!mentorInGroup && !isMentorSearchEnabledRef"
              usage="button"
              type="button"
              color="secondary"
              :text="t('admin.groups.groupStructureModal.body.addMentor')"
              data-test-id="create-groupe_input-add-mentor"
              @emit:click="isMentorSearchEnabledRef = true"
            >
              <template #icon>
                <CrossIcon color="primary" :rotate="45" />
              </template>
            </CustomButton>

            <UsersSearch
              :onMountFocus="true"
              :filterByRole="RoleEnumTypeV2.MENTOR"
              :remove-from-results="mentorInGroup ? [mentorInGroup.user] : []"
              v-if="isMentorSearchEnabledRef"
              :search-callback="mentorSearchCallback"
              @emit:result="addMentor"
            />

            <!-- Loading suggestions -->
            <div v-if="loadingSuggestionsRef" class="suggestions-loading">
              <LoadingIcon color="primary" class="loading-spinner" />
              <span class="loading-text">{{ t('admin.groups.matchingSuggestions.searching') }}</span>
            </div>

            <!-- Matching suggestions collapsible -->
            <div
              v-if="mentorSuggestionsRef.length > 0 && !mentorInGroup"
              class="mentor-suggestions-toggle"
              data-test-id="mentor-suggestions-toggle"
            >
              <div class="suggestions-header" @click="suggestionsExpandedRef = !suggestionsExpandedRef">
                <span class="suggestions-title">
                  💡 {{ t('admin.groups.matchingSuggestions.title', { count: mentorSuggestionsRef.length }) }}
                </span>
                <DropdownIcon color="primary" :deployed="suggestionsExpandedRef" class="toggle-arrow" />
              </div>

              <div v-if="suggestionsExpandedRef" class="suggestions-list">
                <div
                  v-for="mentor in mentorSuggestionsRef"
                  :key="mentor.mentorId"
                  class="suggestion-card"
                  data-test-id="suggestion-card-mentor"
                  @click="selectSuggestedMentor(mentor)"
                >
                  <div class="mentor-info">
                    <img v-if="mentor.picture" :src="PROFILE_PICTURE_BUCKET_URL + mentor.picture" class="mentor-avatar" />
                    <div v-else class="mentor-avatar-placeholder">
                      {{ mentor.firstName[0] }}{{ mentor.lastName[0] }}
                    </div>
                    <div class="mentor-details">
                      <strong class="mentor-name">{{ mentor.firstName }} {{ mentor.lastName }}</strong>
                      <div class="compatibility-score">
                        ⭐ {{ mentor.correspondence }}% {{ t('admin.groups.matchingSuggestions.compatible') }}
                      </div>
                      <div class="group-count-info">
                        {{ mentor.groupCount === 0
                          ? t('admin.groups.matchingSuggestions.noGroup')
                          : `⚠️ ${t('admin.groups.matchingSuggestions.groupCount', { count: mentor.groupCount }, mentor.groupCount)}`
                        }}
                      </div>
                    </div>
                  </div>
                  <p class="match-reason">{{ currentLocale === 'fr' ? mentor.detailsFr : mentor.detailsEn }}</p>
                  <CustomButton
                    usage="button"
                    type="button"
                    data-test-id="button-select-suggested-mentor"
                    color="primary"
                    size="small"
                    :text="t('admin.groups.matchingSuggestions.selectMentor')"
                    @emit:click.stop="selectSuggestedMentor(mentor)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="c-info-wrapper mentee-listing">
            <p class="c-info-label">{{ t('common.mentee.capitalize', 2) }}</p>
            <template v-if="menteesInGroup.length">
              <UserCard
                v-for="mentee in menteesInGroup"
                :user="mentee.user"
                :key="mentee.user.id"
                :groupId="groupRef?.id"
                :subInfo="' '"
              >
                <template #actions>
                  <div
                    class="group-structure-user-remove-btn"
                    data-action=""
                    @click.stop="removeUserRole(mentee.user.id, GroupUserRolesType.MENTEE)"
                  >
                    <CrossIcon data-test-id="btn-remove-user" color="danger" />
                  </div>
                </template>
              </UserCard>
            </template>
            <CustomButton
              v-if="!isMenteeSearchEnabledRef"
              usage="button"
              type="button"
              color="secondary"
              :text="t('admin.groups.groupStructureModal.body.addMentee')"
              data-test-id="create-groupe_input-add-mentee"
              @emit:click="isMenteeSearchEnabledRef = true"
            >
              <template #icon>
                <CrossIcon color="primary" :rotate="45" />
              </template>
            </CustomButton>

            <UsersSearch
              :onMountFocus="true"
              :filterByRole="RoleEnumTypeV2.MENTEE"
              :remove-from-results="menteesInGroup.map((m) => m.user)"
              v-if="isMenteeSearchEnabledRef"
              :search-callback="menteeSearchCallback"
              @emit:result="addMentee"
            />

            <!-- Loading mentee suggestions -->
            <div v-if="loadingMenteeSuggestionsRef" class="suggestions-loading">
              <LoadingIcon color="primary" class="loading-spinner" />
              <span class="loading-text">{{ t('admin.groups.matchingSuggestions.searchingMentees') }}</span>
            </div>

            <!-- Mentee suggestions collapsible -->
            <div
              v-if="menteeSuggestionsRef.length > 0 && menteesInGroup.length === 0"
              class="mentee-suggestions-toggle"
              data-test-id="mentee-suggestions-toggle"
            >
              <div class="suggestions-header" @click="menteeSuggestionsExpandedRef = !menteeSuggestionsExpandedRef">
                <span class="suggestions-title">
                  💡 {{ t('admin.groups.matchingSuggestions.titleMentees', { count: menteeSuggestionsRef.length }) }}
                </span>
                <DropdownIcon color="primary" :deployed="menteeSuggestionsExpandedRef" class="toggle-arrow" />
              </div>

              <div v-if="menteeSuggestionsExpandedRef" class="suggestions-list">
                <div
                  v-for="mentee in menteeSuggestionsRef"
                  :key="mentee.menteeId"
                  class="suggestion-card"
                  data-test-id="suggestion-card-mentee"
                  @click="selectSuggestedMentee(mentee)"
                >
                  <div class="mentor-info">
                    <img v-if="mentee.picture" :src="PROFILE_PICTURE_BUCKET_URL + mentee.picture" class="mentor-avatar" />
                    <div v-else class="mentor-avatar-placeholder">
                      {{ mentee.firstName[0] }}{{ mentee.lastName[0] }}
                    </div>
                    <div class="mentor-details">
                      <strong class="mentor-name">{{ mentee.firstName }} {{ mentee.lastName }}</strong>
                      <div class="compatibility-score">
                        ⭐ {{ mentee.correspondence }}% {{ t('admin.groups.matchingSuggestions.compatible') }}
                      </div>
                      <div class="group-count-info">
                        {{ mentee.groupCount === 0
                          ? t('admin.groups.matchingSuggestions.noGroup')
                          : `⚠️ ${t('admin.groups.matchingSuggestions.groupCount', { count: mentee.groupCount }, mentee.groupCount)}`
                        }}
                      </div>
                    </div>
                  </div>
                  <p class="match-reason">{{ currentLocale === 'fr' ? mentee.detailsFr : mentee.detailsEn }}</p>
                  <CustomButton
                    usage="button"
                    type="button"
                    data-test-id="button-select-suggested-mentee"
                    color="primary"
                    size="small"
                    :text="t('admin.groups.matchingSuggestions.selectMentee')"
                    @emit:click.stop="selectSuggestedMentee(mentee)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="onClose"
          :text="t('ui.button.cancel')"
        ></CustomButton>
        <CustomButton
          v-if="managersInGroup.length > 0 || mentorInGroup !== null || menteesInGroup.length > 0"
          usage="button"
          type="button"
          color="primary"
          data-test-id="create-groupe_submit-create-groupe"
          :isLoading="isSubmittingRef"
          @emit:click="onSubmit"
          :text="!!groupIdRef ? t('ui.button.save') : t('ui.button.create')"
        ></CustomButton>
        <CustomButton
          v-else
          usage="button"
          type="button"
          color="danger"
          @emit:click="onDelete"
          :text="t('ui.button.delete')"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useMutation } from '@tanstack/vue-query'

import {
  addNameToGroupMutation,
  addUserToGroupMutation,
  createGroupMutation,
  removeUserFromGroupMutation,
} from '@/features/admin/groups/api'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { GroupUserRolesType, RoleEnumTypeV2 } from '@/features/auth/types'
import useGroup from '@/features/groups/hooks/use-group.hook'
import { adminSuggestMentorStream, adminSuggestMenteeStream } from '@/features/matching/api'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import type { AdminSuggestMentorResponseType, AdminSuggestMenteeMenteeType } from '@/features/matching/types'
import ProgramsDropdown from '@/features/programs/components/programs-dropdown/programs-dropdown.vue'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CrossIcon from '@/features/ui/components/icons/cross-icon.vue'
import DropdownIcon from '@/features/ui/components/icons/dropdown-icon.vue'
import LoadingIcon from '@/features/ui/components/icons/loading-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import UsersSearch from '@/features/ui/pages/admin/users/users-search/users-search.vue'
import { selectGetAdminGroupStructureModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import UserCard from '@/features/users/components/user-card/user-card.vue'
import useUser from '@/features/users/hooks/use-user.hook'
import type { V2UserAdminDataType, V2UserDataType } from '@/features/users/types'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import { PROFILE_PICTURE_BUCKET_URL } from '@/utils/config/constants'
import { invalidateAdminUsers, invalidateGroup, invalidateGroups } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()

const groupStructureModalRef = selectGetAdminGroupStructureModal(modalsStore)
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const groupIdRef = computed(() => groupStructureModalRef.value.attachedData.groupId ?? null)

const { groupRef, isPendingRef: isGroupPendingRef } = useGroup(groupIdRef)

const { accountRef } = useAccount()
const accountIdRef = computed(() => accountRef.value?.id)
const { userRef: userAccountRef } = useUser(accountIdRef)
const isUserAccountManagerRef = computed(() => userAccountRef.value?.roles?.includes(RoleEnumTypeV2.MANAGER))

const groupUsersRef = ref<{ user: V2UserAdminDataType; role: GroupUserRolesType }[]>([])

const isManagerSearchEnabledRef = ref(false)
const isMentorSearchEnabledRef = ref(false)
const isMenteeSearchEnabledRef = ref(false)

// Matching suggestions state
const { canFillCriteria } = useMatchingSettings(selectedProgramIdRef)
const mentorSuggestionsRef = ref<AdminSuggestMentorResponseType['mentors']>([])
const suggestionsExpandedRef = ref(false)
const loadingSuggestionsRef = ref(false)
const menteeSuggestionsRef = ref<AdminSuggestMenteeMenteeType[]>([])
const menteeSuggestionsExpandedRef = ref(false)
const loadingMenteeSuggestionsRef = ref(false)
const currentLocale = computed(() =>
  typeof window !== 'undefined' ? window.localStorage.getItem('locale') || 'fr' : 'fr',
)

const managersInGroup = computed(() => groupUsersRef.value.filter((u) => u.role === GroupUserRolesType.MANAGER))
const mentorInGroup = computed(() => groupUsersRef.value.find((u) => u.role === GroupUserRolesType.MENTOR) || null)
const menteesInGroup = computed(() => groupUsersRef.value.filter((u) => u.role === GroupUserRolesType.MENTEE))

// if the user is a manager, they are automatically added to the group
watch(
  [isUserAccountManagerRef],
  async () => {
    if (isUserAccountManagerRef.value && userAccountRef.value) {
      const managerAlreadyAdded = managersInGroup.value.some((m) => m.user.id === userAccountRef.value?.id)
      if (!managerAlreadyAdded) {
        groupUsersRef.value.push({
          user: {
            id: userAccountRef.value.id!,
            firstName: userAccountRef.value.firstName!,
            lastName: userAccountRef.value.lastName!,
            picture: userAccountRef.value.picture || null,
            email: userAccountRef.value.email!,
            roles: userAccountRef.value.roles || [],
            hasGroup: true, // User is being added to a group
          },
          role: GroupUserRolesType.MANAGER,
        })
      }
    }
  },
  { immediate: true },
)

watch(
  () => groupRef.value?.users,
  (users) => {
    if (!users) return

    const mappedUsers: { user: V2UserAdminDataType; role: GroupUserRolesType }[] = []

    users.forEach((user) => {
      const userRoles = user.roles || []
      const userData = {
        ...user,
        hasGroup: true, // User is in a group
      } as V2UserAdminDataType

      // Create an entry for each role the user has in the group
      if (userRoles.includes(RoleEnumTypeV2.MANAGER)) {
        mappedUsers.push({ user: userData, role: GroupUserRolesType.MANAGER })
      }
      if (userRoles.includes(RoleEnumTypeV2.MENTOR)) {
        mappedUsers.push({ user: userData, role: GroupUserRolesType.MENTOR })
      }
      if (userRoles.includes(RoleEnumTypeV2.MENTEE)) {
        mappedUsers.push({ user: userData, role: GroupUserRolesType.MENTEE })
      }

      // If user has no specific roles, default to MENTEE
      if (
        !userRoles.includes(RoleEnumTypeV2.MANAGER) &&
        !userRoles.includes(RoleEnumTypeV2.MENTOR) &&
        !userRoles.includes(RoleEnumTypeV2.MENTEE)
      ) {
        mappedUsers.push({ user: userData, role: GroupUserRolesType.MENTEE })
      }
    })

    groupUsersRef.value = mappedUsers
  },
  { immediate: true },
)

const managerSearchCallback = () => {
  // ..
}
const addManager = (user: V2UserDataType | null) => {
  if (user) {
    // Remove only existing MANAGER role for this user (avoid duplicates)
    groupUsersRef.value = groupUsersRef.value.filter(
      (entry) => !(entry.user.id === user.id && entry.role === GroupUserRolesType.MANAGER),
    )

    groupUsersRef.value.push({
      user: {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        picture: user.picture,
        email: user.email,
        roles: user.roles,
        hasGroup: true, // User is being added to a group
      },
      role: GroupUserRolesType.MANAGER,
    })
    isManagerSearchEnabledRef.value = false
  }
}

const mentorSearchCallback = () => {
  // ..
}
const addMentor = async (user: V2UserDataType | null, fromSuggestion = false) => {
  if (user) {
    if (!fromSuggestion) {
      trackEvent(ANALYTICS_EVENTS.MATCHING_ADMIN_OVERRIDE, {
        program_id: selectedProgramIdRef.value,
        role: 'admin',
        action: 'manual_search',
        had_ai_suggestions: mentorSuggestionsRef.value.length > 0,
      })
    }

    // Remove only existing MENTOR role for this user (avoid duplicates)
    groupUsersRef.value = groupUsersRef.value.filter(
      (entry) => !(entry.user.id === user.id && entry.role === GroupUserRolesType.MENTOR),
    )

    groupUsersRef.value.push({
      user: {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        picture: user.picture,
        email: user.email,
        roles: user.roles,
        hasGroup: true, // User is being added to a group
      },
      role: GroupUserRolesType.MENTOR,
    })
    isMentorSearchEnabledRef.value = false

    // Clear mentor suggestions when a mentor is manually selected
    mentorSuggestionsRef.value = []
    suggestionsExpandedRef.value = false

    // Load mentee suggestions if one-to-one and no mentees yet
    if (mentorInGroup.value && menteesInGroup.value.length === 0) {
      await loadMenteeSuggestions(user.id)
    }
  }
}

const menteeSearchCallback = () => {
  // ..
}

// Load mentor suggestions for a mentee (streamed via SSE)
const loadMentorSuggestions = async (menteeId: string) => {
  if (!canFillCriteria.value || !selectedProgramIdRef.value) {
    return
  }

  loadingSuggestionsRef.value = true
  mentorSuggestionsRef.value = []
  suggestionsExpandedRef.value = false

  try {
    await adminSuggestMentorStream(
      {
        programId: selectedProgramIdRef.value,
        menteeId,
      },
      (mentor) => {
        console.log('[loadMentorSuggestions] onMentor received:', mentor.firstName, mentor.lastName, mentor.correspondence)
        mentorSuggestionsRef.value = [...mentorSuggestionsRef.value, mentor].sort(
          (a, b) => b.correspondence - a.correspondence,
        )
        suggestionsExpandedRef.value = true
      },
      () => {
        console.log('[loadMentorSuggestions] onDone - stream complete')
        loadingSuggestionsRef.value = false
      },
      (errorMessage) => {
        console.info('Matching suggestions error:', errorMessage)
        loadingSuggestionsRef.value = false
      },
    )
  } catch (error: any) {
    // Mentee might not have filled criteria - that's OK, continue with manual selection
    console.info('No matching suggestions available:', error.message)
  } finally {
    loadingSuggestionsRef.value = false
  }
}

// Select a suggested mentor
const selectSuggestedMentor = (mentor: AdminSuggestMentorResponseType['mentors'][0]) => {
  trackEvent(ANALYTICS_EVENTS.MATCHING_ADMIN_OVERRIDE, {
    program_id: selectedProgramIdRef.value,
    role: 'admin',
    action: 'select_ai_suggestion',
    had_ai_suggestions: true,
  })

  addMentor({
    id: mentor.mentorId,
    firstName: mentor.firstName,
    lastName: mentor.lastName,
    picture: mentor.picture,
    email: mentor.email,
    roles: [RoleEnumTypeV2.MENTOR],
  }, true)
  // Clear suggestions after selection
  mentorSuggestionsRef.value = []
  suggestionsExpandedRef.value = false
}

// Load mentee suggestions for a mentor (streamed via SSE)
const loadMenteeSuggestions = async (mentorId: string) => {
  if (!canFillCriteria.value || !selectedProgramIdRef.value) {
    return
  }

  loadingMenteeSuggestionsRef.value = true
  menteeSuggestionsRef.value = []
  menteeSuggestionsExpandedRef.value = false

  try {
    await adminSuggestMenteeStream(
      {
        programId: selectedProgramIdRef.value,
        mentorId,
      },
      (mentee) => {
        menteeSuggestionsRef.value = [...menteeSuggestionsRef.value, mentee].sort(
          (a, b) => b.correspondence - a.correspondence,
        )
        menteeSuggestionsExpandedRef.value = true
      },
      () => {
        loadingMenteeSuggestionsRef.value = false
      },
      (errorMessage) => {
        console.info('Matching suggestions error:', errorMessage)
        loadingMenteeSuggestionsRef.value = false
      },
    )
  } catch (error: any) {
    // Mentor might not have filled criteria - that's OK, continue with manual selection
    console.info('No matching suggestions available:', error.message)
  } finally {
    loadingMenteeSuggestionsRef.value = false
  }
}

// Select a suggested mentee
const selectSuggestedMentee = (mentee: AdminSuggestMenteeMenteeType) => {
  trackEvent(ANALYTICS_EVENTS.MATCHING_ADMIN_OVERRIDE, {
    program_id: selectedProgramIdRef.value,
    role: 'admin',
    action: 'select_ai_suggestion',
    had_ai_suggestions: true,
  })

  addMentee({
    id: mentee.menteeId,
    firstName: mentee.firstName,
    lastName: mentee.lastName,
    picture: mentee.picture,
    email: mentee.email,
    roles: [RoleEnumTypeV2.MENTEE],
  }, true)
  // Clear suggestions after selection
  menteeSuggestionsRef.value = []
  menteeSuggestionsExpandedRef.value = false
}

const addMentee = async (user: V2UserDataType | null, fromSuggestion = false) => {
  if (user) {
    if (!fromSuggestion) {
      trackEvent(ANALYTICS_EVENTS.MATCHING_ADMIN_OVERRIDE, {
        program_id: selectedProgramIdRef.value,
        role: 'admin',
        action: 'manual_search',
        had_ai_suggestions: menteeSuggestionsRef.value.length > 0,
      })
    }

    // Remove only existing MENTEE role for this user (avoid duplicates)
    groupUsersRef.value = groupUsersRef.value.filter(
      (entry) => !(entry.user.id === user.id && entry.role === GroupUserRolesType.MENTEE),
    )

    groupUsersRef.value.push({
      user: {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        picture: user.picture,
        email: user.email,
        roles: user.roles,
        hasGroup: true, // User is being added to a group
      },
      role: GroupUserRolesType.MENTEE,
    })
    isMenteeSearchEnabledRef.value = false

    // Clear mentee suggestions when a mentee is manually selected
    menteeSuggestionsRef.value = []
    menteeSuggestionsExpandedRef.value = false

    // NEW: Load mentor suggestions if one-to-one and no mentor yet
    if (menteesInGroup.value.length === 1 && !mentorInGroup.value) {
      await loadMentorSuggestions(user.id)
    }
  }
}

// Remove function - removes only a specific role
const removeUserRole = async (userId: string, role: GroupUserRolesType) => {
  if (role === GroupUserRolesType.MENTOR || role === GroupUserRolesType.MENTEE) {
    trackEvent(ANALYTICS_EVENTS.MATCHING_REMATCH_REQUEST, {
      program_id: selectedProgramIdRef.value,
      role: 'admin',
      removed_role: role === GroupUserRolesType.MENTOR ? 'mentor' : 'mentee',
    })
  }

  groupUsersRef.value = groupUsersRef.value.filter((entry) => !(entry.user.id === userId && entry.role === role))

  // When removing a mentor, reload mentor suggestions if there's still a mentee
  if (role === GroupUserRolesType.MENTOR) {
    menteeSuggestionsRef.value = []
    menteeSuggestionsExpandedRef.value = false

    // Reload mentor suggestions if we still have exactly one mentee
    if (menteesInGroup.value.length === 1) {
      await loadMentorSuggestions(menteesInGroup.value[0].user.id)
    }
  } else if (role === GroupUserRolesType.MENTEE) {
    mentorSuggestionsRef.value = []
    suggestionsExpandedRef.value = false

    // Reload mentee suggestions if we still have a mentor
    if (mentorInGroup.value) {
      await loadMenteeSuggestions(mentorInGroup.value.user.id)
    }
  }
}

// Remove all roles for a user (legacy function)
const removeUser = (userId: string) => {
  groupUsersRef.value = groupUsersRef.value.filter((entry) => entry.user.id !== userId)
}

// Submit
const isSubmittingRef = ref(false)

const onDelete = async () => {
  modalsStore.toggleModal('admin-group-structure', false)

  modalsStore.toggleModal('confirm-action', true)
  modalsStore.updateModalAttachedData('confirm-action', {
    fn: () => {
      onSubmit()
    },
    title: t('moreActions.admin.groups.delete.confirmLabel'),
    content: t('moreActions.admin.groups.delete.confirmContent'),
  })
}

const onSubmit = async () => {
  const currentUsers = groupRef.value?.users ?? []

  // Build Sets of current user roles
  const currentUserRoles = new Map<string, Set<GroupUserRolesType>>()
  currentUsers.forEach((user) => {
    const userId = user.id
    const userRoles = new Set<GroupUserRolesType>()

    if (user.roles?.includes(RoleEnumTypeV2.MANAGER)) userRoles.add(GroupUserRolesType.MANAGER)
    if (user.roles?.includes(RoleEnumTypeV2.MENTOR)) userRoles.add(GroupUserRolesType.MENTOR)
    if (user.roles?.includes(RoleEnumTypeV2.MENTEE)) userRoles.add(GroupUserRolesType.MENTEE)

    // If no roles, default to MENTEE
    if (userRoles.size === 0) userRoles.add(GroupUserRolesType.MENTEE)

    currentUserRoles.set(userId, userRoles)
  })

  // Build Sets of new user roles from the interface
  const newUserRoles = new Map<string, Set<GroupUserRolesType>>()
  groupUsersRef.value.forEach((entry) => {
    const userId = entry.user.id
    if (!newUserRoles.has(userId)) {
      newUserRoles.set(userId, new Set())
    }
    newUserRoles.get(userId)!.add(entry.role)
  })

  // Find all users (current and new)
  const allUserIds = new Set([...currentUserRoles.keys(), ...newUserRoles.keys()])

  // Determine what operations are needed
  const rolesToAdd: { userId: string; role: GroupUserRolesType; userData: any }[] = []
  const rolesToRemove: { userId: string; role: GroupUserRolesType }[] = []

  allUserIds.forEach((userId) => {
    const currentRoles = currentUserRoles.get(userId) || new Set()
    const newRoles = newUserRoles.get(userId) || new Set()

    // Find roles to add (new roles that user didn't have before)
    newRoles.forEach((role) => {
      if (!currentRoles.has(role)) {
        const userEntry = groupUsersRef.value.find((entry) => entry.user.id === userId && entry.role === role)
        if (userEntry) {
          rolesToAdd.push({
            userId,
            role,
            userData: userEntry.user,
          })
        }
      }
    })

    // Find roles to remove (old roles that user no longer has)
    currentRoles.forEach((role) => {
      if (!newRoles.has(role)) {
        rolesToRemove.push({ userId, role })
      }
    })
  })

  console.log('Roles to add:', rolesToAdd)
  console.log('Roles to remove:', rolesToRemove)

  try {
    isSubmittingRef.value = true

    let groupId = groupIdRef.value
    if (!groupId) {
      const created = await createGroupMutation({ programId: selectedProgramIdRef.value! })
      groupId = created.groupId
    }

    // Since backend supports multiple roles, handle users with role changes
    // Group users by those who have any changes (add or remove)
    const usersWithChanges = new Set([...rolesToAdd.map((r) => r.userId), ...rolesToRemove.map((r) => r.userId)])

    // Process each user with changes
    for (const userId of usersWithChanges) {
      const finalRoles = newUserRoles.get(userId) || new Set()

      if (finalRoles.size === 0) {
        // User has no roles left, remove completely
        await removeUserFromGroupMutation({ userId, groupId })
      } else {
        // User has roles, remove them first then add back with all their roles
        const currentRoles = currentUserRoles.get(userId) || new Set()

        // Only remove if user currently exists in group
        if (currentRoles.size > 0) {
          await removeUserFromGroupMutation({ userId, groupId })
        }

        // Add back with all roles they should have
        const rolesArray = Array.from(finalRoles)
        await addUserToGroupMutation({ userId, groupId, roles: rolesArray })
      }
    }

    if (!groupRef.value?.name) {
      await addNameToGroupMutation({ groupId })
    }

    await invalidateGroup(groupId)
    await invalidateGroups(selectedProgramIdRef.value!)
    await invalidateAdminUsers(selectedProgramIdRef.value!)

    toast?.success(
      groupIdRef.value
        ? t('admin.groups.groupStructureModal.existing.sucess')
        : t('admin.groups.groupStructureModal.new.sucess'),
    )

    modalsStore.toggleModal('admin-group-structure', false)
  } catch (e) {
    toast?.error(t('error.common'))
  } finally {
    isSubmittingRef.value = false
  }
}

const onClose = () => {
  modalsStore.toggleModal('admin-group-structure', false)
}
</script>

<style lang="scss">
@import './group-structure-modal-content.scss';
</style>
